import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, NSelect } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { name: "", category: "genie_civil", description: "", base_price: 0, unit: "forfait", active: true };

const ServiceCard = ({ s, admin, onEdit, onDelete }) => (
  <div className={`rounded-md border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300 ${!s.active ? "opacity-60" : ""}`} data-testid={`service-card-${s.id}`}>
    <div className="flex items-start justify-between gap-2">
      <h3 className="text-sm font-semibold text-slate-900">{s.name}</h3>
      {admin && (
        <div className="flex shrink-0 gap-1">
          <button onClick={() => onEdit(s)} className="text-slate-400 hover:text-blue-900" data-testid={`service-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></button>
          <button onClick={() => onDelete(s)} className="text-slate-400 hover:text-red-600" data-testid={`service-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
    <p className="mt-1 text-xs text-slate-500">{s.description}</p>
    <div className="mt-3 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3 text-center">
      <div><p className="font-mono text-sm font-semibold">{s.stats.quotes}</p><p className="text-[10px] uppercase tracking-wider text-slate-400">Devis</p></div>
      <div><p className="font-mono text-sm font-semibold">{s.stats.interventions}</p><p className="text-[10px] uppercase tracking-wider text-slate-400">Interv.</p></div>
      <div><p className="font-mono text-sm font-semibold">{money(s.stats.invoiced)}</p><p className="text-[10px] uppercase tracking-wider text-slate-400">Facturé</p></div>
    </div>
    {s.base_price > 0 && <p className="mt-2 text-xs text-slate-500">Tarif de base : {money(s.base_price)} / {s.unit}</p>}
  </div>
);

export default function Services() {
  const { user } = useAuth();
  const admin = user.role === "admin";
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/services").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { ...EMPTY, ...edit, base_price: Number(edit.base_price) };
    delete body.id; delete body.stats; delete body.created_at;
    try {
      edit.id ? await api.put(`/services/${edit.id}`, body) : await api.post("/services", body);
      toast.success("Service enregistré"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Supprimer « ${s.name} » ?`)) return;
    await api.delete(`/services/${s.id}`); toast.success("Service supprimé"); load();
  };

  return (
    <div>
      <PageHeader kicker="Catalogue & pilotage" title="Services">
        {admin && <Button className="bg-red-600 hover:bg-red-700" onClick={() => setEdit(EMPTY)} data-testid="new-service-btn"><Plus className="mr-1 h-4 w-4" /> Nouveau service</Button>}
      </PageHeader>
      <div className="space-y-10">
        {Object.entries(CATEGORIES).map(([k, c]) => {
          const list = items.filter((s) => s.category === k);
          const invoiced = list.reduce((a, s) => a + s.stats.invoiced, 0);
          return (
            <section key={k} data-testid={`service-category-${k}`} className="fade-up">
              <div className="mb-4 flex flex-wrap items-end justify-between gap-2 border-b-2 pb-2" style={{ borderColor: c.color }}>
                <h2 className="font-display text-lg font-bold text-slate-900">{c.label}</h2>
                <p className="text-xs text-slate-500">{list.length} prestation(s) · {list.reduce((a, s) => a + s.stats.interventions, 0)} intervention(s) · <b>{money(invoiced)}</b> facturés</p>
              </div>
              {list.length ? (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {list.map((s) => <ServiceCard key={s.id} s={s} admin={admin} onEdit={setEdit} onDelete={remove} />)}
                </div>
              ) : <p className="text-sm text-slate-400">Aucune prestation dans ce pôle.</p>}
            </section>
          );
        })}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="service-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifier le service" : "Nouveau service"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Nom *" className="sm:col-span-2"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="service-name-input" /></Field>
              <Field label="Pôle" className="sm:col-span-2">
                <NSelect value={edit.category} onChange={(v) => setEdit({ ...edit, category: v })} testId="service-category-select">
                  {Object.entries(CATEGORIES).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
                </NSelect>
              </Field>
              <Field label="Description" className="sm:col-span-2"><Textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} data-testid="service-description-input" /></Field>
              <Field label="Tarif de base (USD)"><Input type="number" step="any" value={edit.base_price} onChange={(e) => setEdit({ ...edit, base_price: e.target.value })} data-testid="service-price-input" /></Field>
              <Field label="Unité"><Input value={edit.unit} onChange={(e) => setEdit({ ...edit, unit: e.target.value })} data-testid="service-unit-input" /></Field>
              <label className="flex items-center gap-2 text-sm"><Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} data-testid="service-active-switch" /> Service actif</label>
            </div>
          )}
          <DialogFooter><Button className="bg-red-600 hover:bg-red-700" onClick={save} data-testid="service-save-btn">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
